import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Typography, Card, CardContent, Alert, Button } from '@mui/material';
import { Loader } from 'feather-icons-react/build/IconComponents';
import { getCampaignCounts2026 } from '../../api';
import { formatIndianNumber } from '../../utils/helper';
import ThemeOneBg from '../../assets/images/backgrounds/theme_one_bg.png';
import ThemeTwoBg from '../../assets/images/backgrounds/theme_two_bg.png';
import ThemeThreeBg from '../../assets/images/backgrounds/theme_three_bg.png';
import ThemeFourBg from '../../assets/images/backgrounds/stats1.png';
import ThemeFiveBg from '../../assets/images/backgrounds/stats2.png';
import ThemeSixBg from '../../assets/images/backgrounds/stats4.png';
import ThemeSevenBg from '../../assets/images/backgrounds/stats3.png';
import ThemeEightBg from '../../assets/images/backgrounds/stats5.png';

/**
 * Campaign Pillars - 2026.
 *
 * The 2023-2025 tabs of /campaign-dashboard render frozen snapshots (see
 * src/data/campaignPillars2025.js); this one is live, because the campaign is
 * running. Everything comes from a single public call to
 * getCampaignDashboard2026.
 *
 * The metrics, their wording and their order are the client's
 * "Campaign Dashboard_SHS -2026_Sept 17" document. That document is a subset of
 * the 68 indicators in REPORT_EVENT_INDICATOR_KEYS.md, so a figure being absent
 * here does not mean it is not collected - only that the dashboard does not
 * show it.
 *
 * The 2025 card markup repeats a ~40-line Grid pair per row. That is 1,700
 * lines for the same six cards, so this version drives the identical layout
 * from the SECTIONS table below instead.
 */

// What the Advocacy card counts as an event, and who it counts as a
// participant. Listed here rather than derived from SECTIONS because a row
// being on the card does not make it one of these - see the note where the
// totals are built.
const ADVOCACY_EVENT_KEYS = [
  'swachh_bazaar_advocacy_drives',
  'swachh_mohalla_cleanliness_drives',
  'swachh_mohalla_bwg_registration_drives',
  'swachh_offices_cleanliness_drives',
  'swachh_offices_adopting_segregation',
  'clean_water_bodies_cleanliness_drives',
  'ab_do_nahi_chaar_awareness_events',
  'composting_awareness_drives',
  'neki_ki_deewar_rejuvenation_drives',
  'festivities_visarjan_cleanliness_drives',
  'sports_swachhata_competitions',
];

const ADVOCACY_PARTICIPANT_KEYS = [
  'swachh_bazaar_total_participants',
  'clean_water_bodies_total_participants',
  'ab_do_nahi_chaar_total_participants',
  'festivities_total_participants',
  'sports_swachhata_total_participants',
];

const sum = (source, keys) => keys.reduce((total, key) => total + (source[key] || 0), 0);

// Flattens the API payload into the single key space SECTIONS refers to, so a
// row's `key` is the only place a metric name appears.
const flattenCounts = (data) => {
  if (!data) return null;

  const ctu = data.ctu_data || {};
  const cps = data.clean_public_spaces || {};
  const pillars = data.pillar_data || {};
  const shivirs = data.safai_mitra_shivirs_data || {};

  // Urban / rural split of the headline figure. MEA events reported from abroad
  // are counted as urban server-side, so these two normally add up to the
  // headline exactly. The API also returns an `other` bucket for rows with no
  // usable location_type; it is zero today and is not shown.
  const participantsByLocation = data.total_participants_by_location || {};

  return {
    ...pillars,
    total_participants: data.total_participants,
    urban_participants: participantsByLocation.urban,
    rural_participants: participantsByLocation.rural,

    ctu_total: ctu.total_count,
    ctu_transformed: ctu.completed_count,
    ctu_work_in_progress: ctu.incomplete_count,
    ctu_total_participants: ctu.total_participants_sum,

    cps_total: cps.total_count,
    cps_completed: cps.completed_count,
    cps_incomplete: cps.incomplete_count,
    cps_people_engaged: cps.total_people_engaged,

    shivirs_conducted: shivirs.total_number_of_shivirs_conducted,
    shivirs_workers_recognised: shivirs.no_of_sanitation_workers_recognition,
    shivirs_health_camps: shivirs.no_of_preventive_health_checkups_organized,
    shivirs_health_participants: shivirs.health_participants,
    shivirs_ppe_kits: shivirs.no_ppe_kits_safety_gear_distributed,
    shivirs_scheme_beneficiaries: shivirs.total_beneficiaries,

    // The document asks for the Swachh Bharat Diwas total alongside its two
    // halves. The halves are what the closure form collects.
    sbd_total_participants:
      (pillars.sbd_government_staff || 0) + (pillars.sbd_public_participation || 0),

    // Swachh Mohalla and Swachh Offices report two drive counts each rather than
    // a participant count, so the card shows one 'Events' figure per column.
    // NOTE: swachh_offices_adopting_segregation counts OFFICES that adopted
    // 4-stream segregation, not drives conducted; it is added here because the
    // column shows a single Events total.
    swachh_mohalla_events:
      (pillars.swachh_mohalla_cleanliness_drives || 0) +
      (pillars.swachh_mohalla_bwg_registration_drives || 0),
    swachh_offices_events:
      (pillars.swachh_offices_cleanliness_drives || 0) +
      (pillars.swachh_offices_adopting_segregation || 0),

    // Advocacy summary, following the 2025 card's rule: count the drives and
    // events, and separately the people at them. Figures that are neither -
    // offices adopting segregation, stakeholders, partner organisations - stay
    // in their own rows and out of both totals, the same way 2025 leaves out
    // waste-to-art installations and quantity of waste reused.
    advocacy_total_events: sum(pillars, ADVOCACY_EVENT_KEYS),
    advocacy_total_participants: sum(pillars, ADVOCACY_PARTICIPANT_KEYS),
  };
};

/**
 * The cards, in the document's order.
 *
 * `groups` exist only where the document sub-heads a card (the two Advocacy
 * cards). A card with one unnamed group renders as a plain label/value list.
 */
const SECTIONS = [
  {
    title: 'Cleanliness Target Units (CTUs)',
    background: ThemeFourBg,
    groups: [
      {
        rows: [
          { label: 'Total CTUs', key: 'ctu_total' },
          { label: 'CTUs Transformed', key: 'ctu_transformed' },
          { label: 'CTUs Work in Progress', key: 'ctu_work_in_progress' },
          {
            label: 'Total Participants',
            key: 'ctu_total_participants',
          },
        ],
      },
    ],
  },
  {
    title: 'Clean Public Spaces',
    background: ThemeFiveBg,
    groups: [
      {
        rows: [
          { label: 'Total Clean Public Spaces Identified', key: 'cps_total' },
          { label: 'Completed Clean Public Spaces', key: 'cps_completed' },
          { label: 'Incomplete Clean Public Spaces', key: 'cps_incomplete' },
          { label: 'Total People Engaged', key: 'cps_people_engaged' },
        ],
      },
    ],
  },
  {
    title: 'Swachh Paathshala: Yuva for Swachhata',
    background: ThemeThreeBg,
    groups: [
      {
        rows: [
          {
            label: 'Awareness Activities Conducted on SWM Rules 2026',
            key: 'paathshala_awareness_activities',
          },
          {
            label: 'RRR Drives Conducted for Recycling and Re-use',
            key: 'paathshala_rrr_drives',
          },
          {
            label: 'Total Number of Stakeholders Engaged',
            key: 'paathshala_stakeholders_total',
          },
          {
            label: 'Total School Children Participation',
            key: 'paathshala_school_children_participated',
          },
          {
            label: 'Swachhata Gyan Yatra/Exposure Visits Conducted',
            key: 'gyan_yatra_visits_conducted',
          },
          {
            label: 'Swachhata Gyan Yatra/Exposure Visits Participation',
            key: 'gyan_yatra_students_participated',
          },
          { label: 'Swachhata Competitions Organised', key: 'paathshala_competitions_organized' },
          {
            label: 'Swachhata Competitions Participation',
            key: 'paathshala_competitions_participants_recognized',
          },
        ],
      },
    ],
  },
  {
    title: 'SafaiMitra Suraksha Evam Samman Shivir',
    background: ThemeSevenBg,
    groups: [
      {
        rows: [
          { label: 'Shivirs Conducted', key: 'shivirs_conducted' },
          { label: 'Sanitation Workers Recognised', key: 'shivirs_workers_recognised' },
          { label: 'Preventive Health Checkup Camps Organised', key: 'shivirs_health_camps' },
          { label: 'Health Checkup Participants', key: 'shivirs_health_participants' },
          { label: 'PPE Kits Distributed', key: 'shivirs_ppe_kits' },
          { label: 'Scheme Beneficiaries', key: 'shivirs_scheme_beneficiaries' },
        ],
      },
    ],
  },
  // {
  //   title: 'September 27: World Tourism Day',
  //   background: ThemeEightBg,
  //   groups: [
  //     {
  //       rows: [
  //         {
  //           label: 'High Footfall Areas/Tourist Destinations Cleaned',
  //           key: 'tourism_day_areas_cleaned',
  //         },
  //         { label: 'Public Statues Cleaned', key: 'tourism_day_statues_cleaned' },
  //         { label: 'Advocacy Drives for 4-way Segregation', key: 'tourism_day_advocacy_drives' },
  //         { label: 'Total Participants Engaged', key: 'tourism_day_total_participants' },
  //       ],
  //     },
  //   ],
  // },
  // {
  //   title: 'October 2: Swachh Bharat Diwas',
  //   background: ThemeSixBg,
  //   groups: [
  //     {
  //       rows: [
  //         { label: 'Special Gram Sabhas Conducted (Rural)', key: 'gram_sabhas_conducted' },
  //         {
  //           label: 'Special Council Meetings Conducted (Urban)',
  //           key: 'council_meetings_conducted',
  //         },
  //         { label: 'Total Participants', key: 'sbd_total_participants' },
  //         { label: 'Total Government Staff Participation', key: 'sbd_government_staff' },
  //         { label: 'Total Public Participation', key: 'sbd_public_participation' },
  //         { label: "Trees Planted Under 'Ek Ped Maa Ke Naam'", key: 'sbd_trees_planted' },
  //       ],
  //     },
  //   ],
  // },
  {
    // Rendered by AdvocacyCard, not MetricCard: the 2025 card puts its totals
    // across the top and its sub-heads side by side, and this one matches it.
    layout: 'wide',
    title: 'Advocacy',
    subtitle:
      'Under Pillar 1: Transformation of Cleanliness Target Units (CTUs) & Clean Public Spaces, and Pillar 4: Jan Bhagidari for Swachhata',
    background: ThemeOneBg,
    summary: [
      { label: 'Total Events Conducted', key: 'advocacy_total_events' },
      { label: 'Total Participants', key: 'advocacy_total_participants' },
    ],
    groups: [
      {
        heading: 'Swachh Bazaar',
        rows: [
          {
            label: 'Non-Littering Drives Conducted',
            short: 'Events',
            key: 'swachh_bazaar_advocacy_drives',
          },
          {
            label: 'Total Participants Engaged',
            short: 'Participants',
            key: 'swachh_bazaar_total_participants',
          },
        ],
      },
      {
        heading: 'Swachh Mohalla',
        rows: [
          {
            label: 'Cleanliness Drives and BWG Registration Drives for RWAs',
            short: 'Events',
            key: 'swachh_mohalla_events',
          },
        ],
      },
      {
        heading: 'Swachh Offices',
        rows: [
          {
            label: 'Cleanliness Drives and Offices Adopting 4-stream Segregation',
            short: 'Events',
            key: 'swachh_offices_events',
          },
        ],
      },
      {
        heading: 'Clean Water Bodies',
        rows: [
          {
            label: 'Cleanliness Drives Conducted',
            short: 'Events',
            key: 'clean_water_bodies_cleanliness_drives',
          },
          {
            label: 'Total Participants Engaged',
            short: 'Participants',
            key: 'clean_water_bodies_total_participants',
          },
        ],
      },
      {
        heading: '4-Way Waste Segregation: Ab Do Nahi Chaar',
        rows: [
          {
            label: 'Door-to-Door Awareness Events',
            short: 'Events',
            key: 'ab_do_nahi_chaar_awareness_events',
          },
          {
            label: 'Total Participants Engaged',
            short: 'Participants',
            key: 'ab_do_nahi_chaar_total_participants',
          },
        ],
      },
      {
        heading: 'Home, Community and RWA Composting',
        rows: [
          {
            label: 'Composting Awareness Drives',
            short: 'Events',
            key: 'composting_awareness_drives',
          },
          {
            label: 'Total Stakeholders Involved',
            short: 'Participants',
            key: 'composting_stakeholders_distributed',
          },
        ],
      },
      {
        heading: 'Neki Ki Deewar / RRR Centers',
        rows: [
          {
            label: 'Rejuvenation Drives Conducted',
            short: 'Events',
            key: 'neki_ki_deewar_rejuvenation_drives',
          },
          {
            label: 'Local NGOs/CBOs/Partner Organizations Engaged',
            short: 'Participants',
            key: 'neki_ki_deewar_orgs_engaged',
          },
        ],
      },
      {
        heading: 'Swachh Parv, Harit Parv - Clean Green Festivities',
        rows: [
          {
            label: 'Post Festive Cleanliness Drives',
            short: 'Events',
            key: 'festivities_visarjan_cleanliness_drives',
          },
          {
            label: 'Total Participants Engaged',
            short: 'Participants',
            key: 'festivities_total_participants',
          },
        ],
      },
      {
        heading: 'Sports for Swachhata',
        rows: [
          {
            label: 'Sporting Competitions Organized',
            short: 'Events',
            key: 'sports_swachhata_competitions',
          },
          {
            label: 'Total Participants Engaged',
            short: 'Participants',
            key: 'sports_swachhata_total_participants',
          },
        ],
      },
    ],
  },
];

const LABEL_SX = { fontSize: { xs: '0.85rem', sm: '1rem', md: '1rem', lg: '1.1rem' } };
const VALUE_SX = { fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.1rem', lg: '1.3rem' } };

const safeFormat = (value) =>
  typeof value === 'number' && !Number.isNaN(value) ? formatIndianNumber(value) : '--';

/** A number, or the spinner the 2025 card uses while a figure is unavailable. */
const MetricValue = ({ value }) =>
  typeof value === 'number' && value >= 0 ? (
    <Typography fontWeight="bold" color="white" sx={{ ...VALUE_SX, wordBreak: 'break-word' }}>
      {safeFormat(value)}
    </Typography>
  ) : (
    <Loader size={20} color="#ffffff" />
  );

const MetricRow = ({ label, value }) => (
  <>
    <Grid item xs={8} sx={{ textAlign: 'left', display: 'flex', alignItems: 'center' }}>
      <Typography fontWeight="bold" color="white" sx={LABEL_SX}>
        {label}
      </Typography>
    </Grid>
    <Grid
      item
      xs={4}
      sx={{
        textAlign: 'right',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <MetricValue value={value} />
    </Grid>
  </>
);

const MetricCard = ({ section, counts }) => (
  <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
    <Card
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        backgroundImage: `url(${section.background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: 4,
        boxShadow: 3,
        p: { xs: 1, sm: 2 },
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 2 }, width: '100%' }}>
        <Typography
          fontWeight="bold"
          color="white"
          sx={{ fontSize: { xs: '1rem', sm: '1.3rem', md: '1.4rem', lg: '1.7rem' } }}
        >
          {section.title}
        </Typography>
        {section.subtitle && (
          <Typography
            color="white"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, opacity: 0.9, mb: 1 }}
          >
            {section.subtitle}
          </Typography>
        )}

        {section.groups.map((group, groupIndex) => (
          <Box key={group.heading || groupIndex} sx={{ mt: group.heading ? 2 : 1 }}>
            {group.heading && (
              <Typography
                fontWeight="bold"
                color="white"
                sx={{
                  textAlign: 'left',
                  fontSize: { xs: '0.9rem', sm: '1.05rem' },
                  borderBottom: '1px solid rgba(255,255,255,0.4)',
                  pb: 0.5,
                  mb: 1,
                }}
              >
                {group.heading}
              </Typography>
            )}
            <Grid container spacing={1}>
              {group.rows.map((row) => (
                <MetricRow key={row.key} label={row.label} value={counts?.[row.key]} />
              ))}
            </Grid>
          </Box>
        ))}
      </CardContent>
    </Card>
  </Grid>
);

/**
 * The wide card: totals across the top, a rule, then the sub-heads side by side
 * with a divider between them. This is the 2025 Advocacy card's layout, which
 * the client asked 2026 to match; only the figures and sub-heads differ.
 *
 * Five per row at `sm` (xs={2.4} is 12/5), stacked on a phone where the
 * dividers are dropped.
 */
const AdvocacyCard = ({ section, counts }) => (
  <Grid item xs={12}>
    <Card
      sx={{
        width: '100%',
        textAlign: 'center',
        backgroundImage: `url(${section.background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: 4,
        boxShadow: 3,
        p: { xs: 1, sm: 1.5 },
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Typography
          fontWeight="700"
          color="white"
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.6rem', lg: '2rem' },
            mb: 1.5,
            textShadow: '1px 1px 6px #00000055',
          }}
        >
          {section.title}
        </Typography>

        <Grid container spacing={1} justifyContent="center" sx={{ mt: 0.8 }}>
          {section.summary.map((row) => (
            <Grid item xs={12} sm={6} key={row.key}>
              <Typography
                fontWeight="700"
                color="white"
                sx={{ fontSize: { xs: '0.85rem', sm: '1rem', md: '1rem', lg: '1.15rem' } }}
              >
                {row.label}
              </Typography>
              <MetricValue value={counts?.[row.key]} />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ width: '100%', borderBottom: '3px solid #fff', opacity: 0.8, my: 1.5 }} />

        <Grid container spacing={1} justifyContent="center" sx={{ mt: 1 }}>
          {section.groups.map((group, index) => (
            <Grid
              item
              xs={12}
              sm={2.4}
              key={group.heading}
              sx={{
                // No rule after the last cell of a row, the same way the 2025
                // card leaves its fifth column clean.
                borderRight: {
                  xs: 'none',
                  sm: (index + 1) % 5 === 0 ? 'none' : '3px solid rgba(255,255,255,0.8)',
                },
              }}
            >
              <Typography
                fontWeight="700"
                color="white"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1.05rem', md: '1rem', lg: '1.2rem' },
                  mb: 0.5,
                }}
              >
                {group.heading}
              </Typography>
              <Grid container spacing={0.5} justifyContent="center">
                {group.rows.map((row) => (
                  <Grid item xs={group.rows.length === 1 ? 12 : 6} key={row.key}>
                    <Typography
                      fontWeight="600"
                      color="white"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.75rem', lg: '0.9rem' } }}
                    >
                      {row.short || row.label}
                    </Typography>
                    <Typography
                      fontWeight="700"
                      color="white"
                      sx={{
                        fontSize: { xs: '0.85rem', sm: '0.95rem', md: '0.9rem', lg: '1.05rem' },
                        mb: 0.5,
                      }}
                    >
                      {safeFormat(counts?.[row.key])}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  </Grid>
);

/**
 * The headline card. `breakdown` is an optional list of {label, value} pairs
 * shown under the big number - used for the urban / rural split of Total
 * Participants. It is left out of the other hero card, which has no split.
 */
const HeroCard = ({ title, value, background, breakdown }) => (
  <Grid item xs={12}>
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        backgroundImage: `url(${background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 30%',
        backgroundRepeat: 'no-repeat',
        borderRadius: 4,
        boxShadow: 3,
        p: { xs: 1, sm: 1.5 },
      }}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
        <Typography
          fontWeight="700"
          color="white"
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.7rem', lg: '2rem' },
            mb: 1,
            textShadow: '1px 1px 6px #00000055',
          }}
        >
          {title}
        </Typography>
        <Typography
          fontWeight="700"
          color="white"
          sx={{
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.4rem', lg: '2.8rem' },
            textShadow: '1px 1px 6px #00000055',
          }}
        >
          {safeFormat(value)}
        </Typography>

        {breakdown && (
          <Grid container justifyContent="center" spacing={{ xs: 2, sm: 5 }} sx={{ mt: 0.5 }}>
            {breakdown.map((item) => (
              <Grid item key={item.label} xs="auto">
                <Typography
                  fontWeight="600"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.95rem', md: '1rem', lg: '1.1rem' },
                    textShadow: '1px 1px 6px #00000055',
                    opacity: 0.9,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  fontWeight="700"
                  color="white"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem', lg: '1.6rem' },
                    textShadow: '1px 1px 6px #00000055',
                  }}
                >
                  {safeFormat(item.value)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  </Grid>
);

export default function ThemeCount2026() {
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await getCampaignCounts2026();
      setCounts(flattenCounts(data));
    } catch (err) {
      console.error('Error fetching 2026 campaign counts:', err);
      // Leave `counts` as it was: on a refresh failure the page keeps showing
      // the last good figures rather than reverting every card to a spinner.
      setError('Could not load the 2026 campaign figures.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ width: '100%', margin: 'auto', textAlign: 'center' }}>
      <Grid container spacing={2} sx={{ pt: '20px', pb: '30px', px: { xs: '4%', sm: '4%' } }}>
        <Grid item xs={12}>
          <Typography
            className="main-heading"
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.7rem', md: '1.8rem', lg: '2.2rem' },
              fontWeight: 700,
              textAlign: 'center',
              mb: 1,
            }}
          >
            Campaign Pillars - 2026
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={load}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          </Grid>
        )}

        <HeroCard
          title="Total Participants"
          value={counts?.total_participants}
          background={ThemeSixBg}
          breakdown={[
            { label: 'Urban', value: counts?.urban_participants },
            { label: 'Rural', value: counts?.rural_participants },
          ]}
        />
        <HeroCard
          title="National Campaign Roll-out: Total Households Participated"
          value={counts?.national_campaign_households_participated}
          // ThemeTwoBg, not ThemeFiveBg: that one is the Clean Public Spaces
          // card's, and the two read as the same card side by side. Free since
          // the second Advocacy card was merged away.
          background={ThemeTwoBg}
        />

        {SECTIONS.map((section) =>
          section.layout === 'wide' ? (
            <AdvocacyCard key={section.title} section={section} counts={counts} />
          ) : (
            <MetricCard key={section.title} section={section} counts={counts} />
          )
        )}
      </Grid>
    </Box>
  );
}
